import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Image, Video, ArrowLeftRight, MessageSquare, LogOut, Loader2, Phone, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GalleryManager from '../components/GalleryManager';
import VideoManager from '../components/VideoManager';
import BeforeAfterManager from '../components/BeforeAfterManager';
import { getContactMessages } from '../lib/api';
import { toast } from 'sonner';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setMessages(await getContactMessages());
      } catch (e) {
        toast.error('Error al cargar mensajes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#60A5FA] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Mensajes de contacto</h2>
        <p className="text-white/60 text-sm">{messages.length} mensajes recibidos</p>
      </div>
      {messages.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          <MessageSquare className="w-12 h-12 mx-auto mb-4" />
          <p>No hay mensajes todavía.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className="bg-[#0F1D33]/50 border border-white/10 rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="text-white font-semibold">{msg.name}</p>
                    <span className="text-[#60A5FA] text-sm flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {msg.phone}
                    </span>
                  </div>
                  <p className="text-white/70 text-sm">{msg.message}</p>
                  <p className="text-white/30 text-xs mt-2">
                    {new Date(msg.timestamp).toLocaleString('es-EC')}
                  </p>
                </div>
                <a
                  href={`https://wa.me/593${msg.phone.replace(/^0/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 px-4 py-2 bg-[#25D366]/20 text-[#25D366] rounded-lg text-sm font-medium hover:bg-[#25D366]/30 transition-colors flex items-center gap-1"
                >
                  Responder
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('gallery');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const tabs = [
    { id: 'gallery', label: 'Galería', icon: Image },
    { id: 'beforeafter', label: 'Antes/Después', icon: ArrowLeftRight },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'messages', label: 'Mensajes', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-[#0A1628]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0F1D33]/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/lavaporcar-logo.png" alt="Lavaporcar" className="h-12 w-auto" />
            <div>
              <h1 className="text-white font-bold">Panel de Administración</h1>
              <p className="text-white/50 text-xs">Gestión de contenido</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#60A5FA] text-sm flex items-center gap-1 transition-colors"
            >
              Ver sitio
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-5 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white shadow-lg'
                    : 'bg-[#0F1D33]/50 text-white/60 hover:text-white border border-white/10'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'gallery' && <GalleryManager />}
          {activeTab === 'beforeafter' && <BeforeAfterManager />}
          {activeTab === 'videos' && <VideoManager />}
          {activeTab === 'messages' && <ContactMessages />}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
